/**
 * GalleryLayout - Shared layout for all gallery sub-routes.
 *
 * Provides:
 * - Single "Back to Galleries" navigation (eliminates duplicate back buttons)
 * - Gallery title with human-readable label
 * - Status badge
 * - Segmented sub-navigation (Overview, Review, Job Details, Export)
 * - GalleryDetailContext provider wrapper
 *
 * This is the main layout component for the new gallery V2 experience.
 *
 * @see /src/App.tsx - Route configuration
 * @see /src/features/gallery/context/GalleryDetailContext.tsx - State provider
 */

import { Outlet, useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, ExternalLink, Loader2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { GalleryDetailProvider, useGalleryDetail } from '../context/GalleryDetailContext'
import { GallerySubNav } from '../components/shared/GallerySubNav'
import { GalleryStatusBadge } from '../components/shared/GalleryStatusBadge'
import { buildJobberJobUrl } from '@/features/jobber'

/**
 * Animation variants for page transitions.
 */
const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
}

/**
 * Inner layout content that uses the context.
 * Separated to ensure context is available.
 */
function GalleryLayoutContent() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const basePath = `/media/galleries/${id}`

  const {
    gallery,
    stats,
    isLoading,
    error,
    jobImages,
    enrichment,
    jobLinkage,
    approveGallery,
    rejectGallery,
  } = useGalleryDetail()

  // Build Jobber profile URL from available data
  const jobberProfileUrl = buildJobberJobUrl({
    jobberWebUri: jobImages[0]?.jobber_web_uri,
    jobNumber: gallery?.job_number ?? jobImages[0]?.job_number,
    jobId: jobLinkage?.job_id ?? enrichment?.job_id,
  })

  // Format gallery title - prefer title, fall back to job number, then ID
  const displayTitle = gallery?.title
    || (gallery?.job_number ? `Job #${gallery.job_number}` : null)
    || `Gallery ${id}`

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <header className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
          <div className="px-6 py-4">
            <div className="flex items-center gap-4 mb-4">
              <Skeleton className="h-9 w-32" />
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-6 w-20" />
            </div>
            <Skeleton className="h-10 w-96" />
          </div>
        </header>
        <main className="flex-1 p-6">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </main>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col h-full">
        <header className="sticky top-0 z-10 bg-background border-b">
          <div className="px-6 py-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/media/galleries')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Galleries
            </Button>
          </div>
        </header>
        <main className="flex-1 p-6">
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
            <h2 className="text-lg font-semibold mb-2">Failed to load gallery</h2>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => navigate('/media/galleries')}>
              Return to Gallery List
            </Button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Sticky header with navigation */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="px-4 py-3 sm:px-6 sm:py-4 space-y-3 sm:space-y-4">
          {/* Top row: Back button + title + status + actions */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              {/* Single back navigation */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/media/galleries')}
                className="gap-2 shrink-0 h-8 sm:h-9"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back</span>
                <span className="hidden lg:inline"> to Galleries</span>
              </Button>

              {/* Gallery title */}
              <h1 className="text-lg sm:text-xl font-semibold truncate flex-1 sm:flex-none">
                {displayTitle}
              </h1>

              {/* Status badge */}
              {gallery?.review_status && (
                <div className="shrink-0 scale-90 sm:scale-100 origin-left">
                  <GalleryStatusBadge
                    status={gallery.review_status}
                    showIcon
                    size="md"
                  />
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Gallery approval actions */}
              {gallery?.review_status !== 'approved' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={approveGallery}
                  className="gap-2 h-8 sm:h-9"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">Approve</span>
                </Button>
              )}
              {gallery?.review_status !== 'rejected' && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={rejectGallery}
                  className="gap-2 h-8 sm:h-9"
                >
                  <XCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">Reject</span>
                </Button>
              )}
              {jobberProfileUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="gap-2 h-8 sm:h-9 flex-1 sm:flex-none"
                >
                  <a
                    href={jobberProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Jobber</span>
                  </a>
                </Button>
              )}
            </div>
          </div>

          {/* Sub-navigation */}
          <div className="flex items-center justify-between">
            <GallerySubNav
              basePath={basePath}
              pendingCount={stats.pending}
              galleryStatus={gallery?.review_status}
            />

            {/* Meta info */}
            <div className="hidden md:flex items-center gap-4 text-sm text-muted-foreground">
              {gallery?.franchise_region && (
                <span className="capitalize">{gallery.franchise_region.replace('_', ' ')}</span>
              )}
              {gallery?.job_number && (
                <span>Job #{gallery.job_number}</span>
              )}
              <span>{stats.total} images</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main content area - renders child routes */}
      <main className="flex-1 overflow-auto">
        <motion.div
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2 }}
          className="h-full"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  )
}

/**
 * GalleryLayout - Main export with provider wrapper.
 */
export default function GalleryLayout() {
  return (
    <GalleryDetailProvider>
      <GalleryLayoutContent />
    </GalleryDetailProvider>
  )
}
